import { ACTION_LABELS, RangeData, RangeFile } from './types';

const TOTAL_TOLERANCE = 0.05;
const VALID_ACTIONS = new Set(Object.keys(ACTION_LABELS));
const RAISE_PATTERN = /^R\d+(\.\d+)?$/;

export class RangeValidationError extends Error {
   constructor(public readonly fileLabel: string, public readonly issues: string[]) {
      super(`Invalid range data in ${fileLabel}: ${issues[0] ?? 'Unknown issue'}`);
      this.name = 'RangeValidationError';
   }
}

function isRecord(value: unknown): value is Record<string, unknown> {
   return typeof value === 'object' && value !== null;
}

interface RangeValidationOptions {
   requiredPlayers?: number[];
}

export function validateRangeFile(fileLabel: string, data: unknown, options?: RangeValidationOptions): RangeFile {
   const issues: string[] = [];

   if (!isRecord(data)) {
      throw new RangeValidationError(fileLabel, ['File is not a JSON object']);
   }

   const playersInfo = data.players_info;
   if (!Array.isArray(playersInfo) || playersInfo.length === 0) {
      throw new RangeValidationError(fileLabel, ['Missing players_info array']);
   }

   const normalizedPlayers: Array<{ simple_hand_counters: RangeData }> = [];
   const requiredPlayers = options?.requiredPlayers ?? playersInfo.map((_, idx) => idx);

   playersInfo.forEach((playerInfo, playerIndex) => {
      if (!isRecord(playerInfo)) {
         issues.push(`players_info[${playerIndex}] is not an object`);
         return;
      }

      const counters = playerInfo.simple_hand_counters;
      if (!isRecord(counters)) {
         issues.push(`players_info[${playerIndex}].simple_hand_counters is missing`);
         return;
      }

      const typedCounters = counters as RangeData;
      const shouldValidate = requiredPlayers.includes(playerIndex);

      Object.entries(typedCounters).forEach(([handKey, handValue]) => {
         if (!isRecord(handValue)) {
            issues.push(`Hand ${handKey} for player ${playerIndex} is not an object`);
            return;
         }

         if (shouldValidate) {
            if (!('f' in handValue) || !isRecord(handValue.f)) {
               issues.push(`Hand ${handKey} for player ${playerIndex} is missing frequency map 'f'`);
               return;
            }

            const freq = handValue.f as Record<string, unknown>;
            let total = 0;
            Object.entries(freq).forEach(([action, value]) => {
               const numericValue = typeof value === 'number' ? value : Number.NaN;
               if (Number.isNaN(numericValue)) {
                  issues.push(`Hand ${handKey}/${action} for player ${playerIndex} is not a number`);
                  return;
               }
               if (numericValue < 0 || numericValue > 1 + 1e-6) {
                  issues.push(`Hand ${handKey}/${action} for player ${playerIndex} is outside 0-1 range`);
               }
               if (!VALID_ACTIONS.has(action) && !RAISE_PATTERN.test(action)) {
                  issues.push(`Hand ${handKey} references unknown action '${action}'`);
               }
               total += numericValue;
            });

            const shouldValidateTotal = total > 0;
            if (shouldValidateTotal && Math.abs(1 - total) > TOTAL_TOLERANCE) {
               issues.push(`Hand ${handKey} frequencies for player ${playerIndex} do not sum to ~1`);
            }
         }

         if (!('hand_ev' in handValue) || typeof handValue.hand_ev !== 'number') {
            issues.push(`Hand ${handKey} for player ${playerIndex} is missing numeric hand_ev`);
         }
      });

      normalizedPlayers.push({ simple_hand_counters: typedCounters });
   });

   if (issues.length > 0) {
      throw new RangeValidationError(fileLabel, issues);
   }

   return { players_info: normalizedPlayers };
}
